import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import { Add, Delete, Edit, Save, Close, CheckCircle } from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';
import useApi from 'hooks/useApi';
import { PERMISSIONS } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import RuleBuilder from './detection/RuleBuilder';
import PreviewPanel from './detection/PreviewPanel';
import {
  CHECK_TYPES,
  CheckType,
  DETERMINISTIC_CHECK_TYPES,
  RuleSpec,
  describeRule,
  isAiCheck,
  emptySpec,
  validateSpec
} from 'utils/detectionCheckTypes';

// ============================================================================
// INTERFACES
// ============================================================================

interface DetectionSettingsProps {
  teamUid: string;
  clientUID?: string;
}

interface SourceConfig {
  sourceType:
    | 'step_text_field'
    | 'step_image_field'
    | 'step_location'
    | 'step_date_field'
    | 'step_submission_date'
    | 'resource_type_field'
    | 'custom';
  stepUid?: string;
  fieldUid?: string;
  resourceTypeId?: string;
  customValue?: string;
  customType?: 'text' | 'image';
}

interface NotificationConfig {
  users: string[];
  roles: string[];
  channels: string[];
  transactionOnly?: boolean;
  // Which outcomes are worth telling someone about. Absent/empty means "all",
  // preserving how rules saved before this field behaved — new rules default to
  // No Match + Inconclusive, which is what people actually want to hear about.
  onOutcomes?: string[];
}

interface DetectionCondition {
  uid?: string;
  name: string;
  sources: SourceConfig[];
  instruction: string;
  notification: NotificationConfig;
  enabled: boolean;
  // Run the AI twice; if the runs disagree a stronger model decides. Off unless
  // an admin turns it on for this rule — it at least doubles the cost.
  doubleCheck?: boolean;
  // When present this IS the rule — a typed spec evaluated by code, with
  // `sources`/`instruction` ignored. Absent means the legacy prompt form,
  // which stays supported indefinitely for checks that need judgement.
  spec?: RuleSpec | null;
  // Publish lifecycle. `publishState` rather than `status` because the model's
  // BaseSchema already owns `status` as the soft-delete flag.
  publishState?: 'draft' | 'published' | 'archived';
  specVersion?: number;
  published?: any;
  lastPreview?: any;
  // Server-computed. The dashboard has no way to recompute the rule
  // fingerprint, so whether a rule has unpublished edits — or has been
  // previewed since its logic last changed — has to come from the API.
  _state?: {
    hasUnpublishedChanges: boolean;
    previewedForCurrentLogic: boolean;
    liveVersion: number;
  };
}

// ============================================================================
// CONSTANTS & HELPERS
// ============================================================================

const getInputTag = (index: number) => `{input${index + 1}}`;

const TEMPLATE_INSTRUCTIONS = [
  {
    label: 'Face match',
    value: '{input1} is an ID/license photo. {input2} is a live selfie. Check if the person in {input2} matches the person in {input1}.'
  },
  { label: 'Text contains value', value: 'Check if {input1} contains the text value of {input2}.' },
  { label: 'Signature check', value: 'Check if {input1} has a valid signature in it.' },
  { label: 'Document authenticity', value: 'Check if {input1} appears to be an authentic, unaltered document.' },
  { label: 'ID card validation', value: 'Check if {input1} is a valid government-issued ID card.' },
  { label: 'Text match', value: 'Compare the text in {input1} with {input2} and check if they convey the same information.' },
  { label: 'Image quality', value: 'Check if {input1} is a clear, high-quality image suitable for verification purposes.' },
  { label: 'Location proximity', value: 'Check if the completions of {input1} and {input2} are done within {distance} km of each other.' },
  { label: 'Date within range', value: 'Check if {input1} falls on or before {input2}.' },
  { label: 'Step submitted on time', value: 'Check if {input1} (the step submission date) is within 24 hours of {input2}.' }
];

const NOTIFICATION_CHANNELS = ['EMAIL', 'SMS', 'IN-APP', 'PRIORITY', 'ALERT'];

const isFaceComparisonInstruction = (instruction: string): boolean => {
  if (!instruction) return false;
  const lower = instruction.toLowerCase();
  if (lower.includes('id/license photo') && lower.includes('live selfie')) return true;
  if (lower.includes('id photo') && lower.includes('selfie')) return true;
  const hasFace = ['face', 'facial', 'selfie'].some((k) => lower.includes(k));
  const hasMatch = ['match', 'matches', 'same person', 'person in', 'compare'].some((k) => lower.includes(k));
  return hasFace && hasMatch;
};

const OUTCOME_OPTIONS: { value: string; label: string }[] = [
  { value: 'false', label: 'No Match' },
  { value: 'other', label: 'Inconclusive' },
  { value: 'true', label: 'Match' }
];

const emptySource = (): SourceConfig => ({ sourceType: 'custom', customValue: '', customType: 'text' });
const emptyNotification = (): NotificationConfig => ({
  users: [],
  roles: [],
  channels: [],
  transactionOnly: false,
  // New rules notify on the outcomes that need action. A rule that also pings
  // on every Match trains people to ignore it.
  onOutcomes: ['false', 'other']
});
// Every rule the dashboard creates — structured or prompt — starts as a draft
// and does not evaluate until it has been previewed and published.
const emptyCondition = (): DetectionCondition => ({
  name: '',
  sources: [emptySource()],
  instruction: '',
  notification: emptyNotification(),
  enabled: true,
  doubleCheck: false,
  spec: null,
  publishState: 'draft',
  specVersion: 0,
  published: null,
  lastPreview: null
});

// Structured is the default for new rules; the prompt form is reachable but
// never the first thing offered.
const emptyStructuredCondition = (checkType: CheckType = 'TEXT_COMPARE'): DetectionCondition => ({
  ...emptyCondition(),
  spec: emptySpec(checkType),
  // New rules start as drafts and do not evaluate until published.
  publishState: 'draft'
});

const safeCondition = (c: any): DetectionCondition => ({
  uid: c?.uid,
  name: c?.name || '',
  sources: Array.isArray(c?.sources) && c.sources.length > 0 ? c.sources.map((s: any) => ({ ...emptySource(), ...s })) : [emptySource()],
  instruction: c?.instruction || '',
  spec: c?.spec && CHECK_TYPES[c.spec.checkType as CheckType] ? (c.spec as RuleSpec) : null,
  // Absent means a rule from before the lifecycle existed — those are live,
  // exactly as they have always been.
  publishState: c?.publishState || 'published',
  specVersion: c?.specVersion || 0,
  published: c?.published || null,
  lastPreview: c?.lastPreview || null,
  _state: c?._state,
  notification: {
    ...emptyNotification(),
    ...(c?.notification || {}),
    // A rule saved before onOutcomes existed notifies on everything. Show that
    // honestly (all three ticked) rather than letting the new-rule default
    // silently switch off its Match alerts the next time someone hits save.
    onOutcomes:
      Array.isArray(c?.notification?.onOutcomes) && c.notification.onOutcomes.length > 0
        ? c.notification.onOutcomes
        : c?.uid
        ? OUTCOME_OPTIONS.map((o) => o.value)
        : emptyNotification().onOutcomes
  },
  enabled: c?.enabled !== undefined ? c.enabled : true,
  doubleCheck: !!c?.doubleCheck
});

// ============================================================================
// COMPONENT
// ============================================================================

const DetectionSettings: React.FC<DetectionSettingsProps> = ({ teamUid, clientUID }) => {
  const apiContext = useContext(ApiContext);
  const { showToast } = useApi();
  const formRef = useRef<HTMLDivElement>(null);

  const [conditions, setConditions] = useState<DetectionCondition[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [users, setUsers] = useState<{ uid: string; name: string }[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  // Plain-English drafting. The model proposes a spec; the user reviews it in
  // the normal form before anything is saved.
  const [draftRequest, setDraftRequest] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [draftAssumptions, setDraftAssumptions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state: null = no form, condition object = editing/creating
  const [editingCondition, setEditingCondition] = useState<DetectionCondition | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); // null = new, number = editing existing

  useEffect(() => {
    if (teamUid) loadData();
  }, [teamUid]);

  /**
   * `silent` refreshes the data WITHOUT flipping the page-level loading flag.
   *
   * That flag unmounts the whole form (see the `if (loading)` guard below), so a
   * non-silent refresh while someone is mid-edit destroys the open form and any
   * preview result sitting in it. Anything that runs behind an open form —
   * after a save, after a preview — must pass silent.
   */
  const loadData = async (silent = false) => {
    if (!apiContext || !teamUid) {
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        apiContext.getDetectionConfig(teamUid),
        apiContext.getResourceTypes(clientUID),
        apiContext.getFlowByTeamUid(teamUid),
        apiContext.getDefaultSteps(teamUid),
        apiContext.getTeamUsers(teamUid)
      ]);
      const configRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const configData = configRes?.data;
      const configArray = Array.isArray(configData) ? configData : Array.isArray(configRes) ? configRes : [];
      setConditions(configArray.map(safeCondition));

      const rtData = results[1].status === 'fulfilled' ? results[1].value?.data : null;
      setResourceTypes(Array.isArray(rtData) ? rtData : []);

      const flowSteps = results[2].status === 'fulfilled' ? results[2].value?.data?.steps : null;
      // Role-based default steps can also be detection sources. Normalize them to
      // the flow-step shape ({ uid, title, stepType, component.fields }) and tag the
      // title so admins can distinguish them in the step picker. getStepFields and
      // the step <Select>s then work for both without further changes.
      const dsRes = results[3].status === 'fulfilled' ? results[3].value?.data ?? results[3].value : null;
      const defaultSteps = (Array.isArray(dsRes) ? dsRes : []).map((d: any) => ({
        uid: d.uid,
        title: `${d.title} (Default Step)`,
        stepType: d.stepType,
        component: { componentType: d.stepType, fields: d.fields || [] }
      }));
      setSteps([...(Array.isArray(flowSteps) ? flowSteps : []), ...defaultSteps]);

      // Same normalization MonitoringSettings uses — the team-users endpoint
      // returns mixed shapes, some wrapping the user under `.user` and some
      // flattening it.
      const usersRaw = results[4].status === 'fulfilled' ? (results[4].value as any)?.data : null;
      apiContext
        .getDetectionProviders()
        .then((res: any) => setProviders(Array.isArray(res?.data) ? res.data : []))
        .catch(() => setProviders([]));

      setUsers(
        (Array.isArray(usersRaw) ? usersRaw : [])
          .map((entry: any) => {
            const u = entry?.user ?? entry;
            const fullName = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
            return {
              uid: u?.uid,
              name: fullName || u?.name || u?.email || u?.username || ''
            };
          })
          .filter((u: { uid: string; name: string }) => u.uid && u.name)
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ==================== ACTIONS ====================

  const handleAdd = (checkType?: CheckType) => {
    // Default to a structured rule. The prompt form is still reachable, but
    // it is no longer what someone lands on by default.
    setEditedSincePreview(false);
    setPreviewedThisSession(false);
    setEditingCondition(checkType ? emptyStructuredCondition(checkType) : emptyCondition());
    setEditingIndex(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEdit = (index: number) => {
    setEditedSincePreview(false);
    setPreviewedThisSession(false);
    setEditingCondition({ ...conditions[index] });
    setEditingIndex(index);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleCancel = () => {
    setEditingCondition(null);
    setEditingIndex(null);
  };

  const handleDelete = async (index: number) => {
    const condition = conditions[index];
    if (condition.uid && apiContext) {
      try {
        await apiContext.deleteDetectionConfig(teamUid, condition.uid);
        showToast('Condition deleted', 'success');
      } catch (err) {
        showToast('Failed to delete', 'error');
      }
    }
    const updated = conditions.filter((_, i) => i !== index);
    setConditions(updated);
    if (editingIndex === index) handleCancel();
  };

  const handleSave = async () => {
    if (!apiContext || !editingCondition) return;
    setSaving(true);
    try {
      // Send one form or the other, never both — a structured rule carrying a
      // leftover `instruction` would look prompt-driven to anyone reading the
      // document, and the backend ignores whichever one isn't in play.
      const lifecycle = {
        publishState: editingCondition.publishState || 'draft'
        // `published` / `lastPreview` / `specVersion` are server-owned — sending
        // them back would let the client fake having previewed a rule.
      };
      const payload: any = editingCondition.spec
        ? {
            uid: editingCondition.uid,
            name: editingCondition.name,
            enabled: editingCondition.enabled,
            doubleCheck: !!editingCondition.doubleCheck,
            notification: editingCondition.notification,
            spec: editingCondition.spec,
            sources: [],
            instruction: '',
            ...lifecycle
          }
        : {
            uid: editingCondition.uid,
            name: editingCondition.name,
            enabled: editingCondition.enabled,
            doubleCheck: !!editingCondition.doubleCheck,
            notification: editingCondition.notification,
            sources: editingCondition.sources,
            instruction: editingCondition.instruction,
            spec: null,
            ...lifecycle
          };

      let res: any;
      if (editingCondition.uid) {
        res = await apiContext.updateDetectionConfig(teamUid, editingCondition.uid, payload);
      } else {
        res = await apiContext.createDetectionConfig(teamUid, payload);
      }
      // The backend now shape-checks the rule and returns a message written for
      // whoever is editing it ("Input 2 needs both a step and a field"). Surface
      // that instead of a generic failure, or the author has no idea what to fix.
      if (res?.success === false) {
        showToast(typeof res?.message === 'string' ? res.message : 'Failed to save', 'error');
        return;
      }

      const savedData = res?.data;
      const safe = safeCondition(savedData?.uid ? savedData : { ...editingCondition, uid: savedData?.uid || editingCondition.uid });

      const wasNew = editingIndex === null;
      if (!wasNew) {
        const updated = [...conditions];
        updated[editingIndex as number] = safe;
        setConditions(updated);
      } else {
        setConditions([...conditions, safe]);
      }

      // Stay open. Saving is a step in building a rule, not the end of it —
      // closing here forced people back through Edit just to test what they had
      // only just finished writing.
      setEditingCondition({ ...editingCondition, uid: safe.uid, publishState: safe.publishState });
      setEditingIndex(wasNew ? conditions.length : editingIndex);

      // A preview taken before the rule had a uid couldn't be recorded against
      // anything, so the rule would still read as untested. Nudge the panel
      // to re-run the same test now that there is something to record it on.
      if (wasNew && safe.uid) setRerunPreviewToken((t) => t + 1);

      showToast('Saved. Publish when you are happy — testing first is recommended.', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || err?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    if (!apiContext || !draftRequest.trim()) return;
    setDrafting(true);
    setDraftAssumptions([]);
    try {
      const res: any = await apiContext.draftDetectionRule(teamUid, draftRequest, steps, resourceTypes);
      if (res?.success === false || !res?.data?.spec) {
        showToast(typeof res?.message === 'string' ? res.message : 'Could not draft a rule from that', 'error');
        return;
      }
      // Straight into the normal form, pre-filled and flagged. Nothing is saved
      // until the author looks at the generated sentence and accepts it.
      setEditedSincePreview(true);
      setEditingCondition({
        ...emptyCondition(),
        name: draftRequest.slice(0, 60),
        spec: res.data.spec
      });
      setEditingIndex(null);
      setDraftAssumptions(Array.isArray(res.data.assumptions) ? res.data.assumptions : []);
      setDraftRequest('');
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      showToast(err?.response?.data?.message || err?.message || 'Could not draft a rule', 'error');
    } finally {
      setDrafting(false);
    }
  };

  // ==================== PUBLISH ====================

  const [publishing, setPublishing] = useState(false);
  // Edits made since the form was opened or last previewed. The server holds
  // the authoritative fingerprint check; this only keeps the button from
  // looking available a moment before the server refuses.
  const [editedSincePreview, setEditedSincePreview] = useState(false);
  // Bumped to ask PreviewPanel to re-run its last test — used after a first save,
  // when an earlier preview had no saved rule to be recorded against.
  const [rerunPreviewToken, setRerunPreviewToken] = useState(0);
  // A preview of the CURRENT logic that this session has seen succeed. The
  // server is still the authority on publish; this only keeps the button from
  // looking dead in the gap before `_state` refreshes.
  const [previewedThisSession, setPreviewedThisSession] = useState(false);

  /**
   * True once a rule has a published snapshot — from then on edits wait for a
   * re-publish. A legacy rule has none, so its saves go live immediately, which
   * is exactly how it has always behaved and must keep behaving.
   */
  const isLegacyGated = (c: DetectionCondition | null): boolean => !!c?.published;

  /** Saves on this rule take effect the moment they land. */
  const savesGoLiveImmediately = (c: DetectionCondition | null): boolean => !!c?.uid && !c?.published && c?.publishState !== 'draft';

  /** Testing is recommended, not required — this only drives the warning chip. */
  const isTested = (c: DetectionCondition | null): boolean =>
    !!c?.uid && (previewedThisSession || !!c?._state?.previewedForCurrentLogic) && !editedSincePreview;

  const hasUnpublishedEdits = (c: DetectionCondition | null): boolean => !!c?._state?.hasUnpublishedChanges || editedSincePreview;

  const handlePublish = async () => {
    if (!apiContext || !editingCondition?.uid) return;
    setPublishing(true);
    try {
      const res: any = await apiContext.publishDetectionConfig(teamUid, editingCondition.uid);
      if (res?.success === false) {
        // Surface the backend's reason verbatim (e.g. the rule isn't valid yet).
        showToast(typeof res?.message === 'string' ? res.message : 'Could not publish', 'error');
        return;
      }
      showToast(res?.message || 'Published', 'success');
      await loadData();
      setEditingCondition(null);
      setEditingIndex(null);
    } catch (err: any) {
      showToast(err?.response?.data?.message || err?.message || 'Could not publish', 'error');
    } finally {
      setPublishing(false);
    }
  };

  // ==================== FORM STATE UPDATERS ====================

  const updateField = (updates: Partial<DetectionCondition>) => {
    if (!editingCondition) return;
    // Only logic changes invalidate a preview — renaming a rule or changing a
    // recipient doesn't, which matches what ruleHash() fingerprints server-side.
    if ('spec' in updates || 'sources' in updates || 'instruction' in updates) {
      setEditedSincePreview(true);
      setPreviewedThisSession(false);
    }
    setEditingCondition({ ...editingCondition, ...updates });
  };

  const updateSource = (srcIndex: number, updates: Partial<SourceConfig>) => {
    if (!editingCondition) return;
    const sources = [...editingCondition.sources];
    sources[srcIndex] = { ...sources[srcIndex], ...updates };
    setEditingCondition({ ...editingCondition, sources });
  };

  const addSource = () => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, sources: [...editingCondition.sources, emptySource()] });
  };

  const removeSource = (srcIndex: number) => {
    if (!editingCondition || editingCondition.sources.length <= 1) return;
    setEditingCondition({ ...editingCondition, sources: editingCondition.sources.filter((_, i) => i !== srcIndex) });
  };

  const updateNotification = (updates: Partial<NotificationConfig>) => {
    if (!editingCondition) return;
    setEditingCondition({ ...editingCondition, notification: { ...editingCondition.notification, ...updates } });
  };

  // ==================== VALIDATION ====================

  const isSourceValid = (s: SourceConfig): boolean => {
    if (!s.sourceType) return false;
    if (s.sourceType === 'step_text_field' || s.sourceType === 'step_image_field' || s.sourceType === 'step_date_field')
      return !!(s.stepUid && s.fieldUid);
    if (s.sourceType === 'step_location' || s.sourceType === 'step_submission_date') return !!s.stepUid;
    if (s.sourceType === 'resource_type_field') return !!(s.resourceTypeId && s.fieldUid);
    if (s.sourceType === 'custom') return !!(s.customValue && s.customValue.trim());
    return false;
  };

  // A rule is only evaluated when a step it references is submitted, so one
  // built entirely from profile/custom values would save fine and then never
  // run. Catch it here as well as server-side.
  const STEP_BOUND_TYPES = ['step_text_field', 'step_image_field', 'step_location', 'step_date_field', 'step_submission_date'];
  const hasStepBoundSource = (c: DetectionCondition | null): boolean => !!c?.sources?.some((s) => STEP_BOUND_TYPES.includes(s.sourceType));

  // Placeholders are matched to inputs positionally at evaluation time, so
  // {input5} on a two-input rule reaches the model as a reference to nothing.
  const danglingPlaceholders = (c: DetectionCondition | null): number[] => {
    if (!c) return [];
    const referenced = [...(c.instruction || '').matchAll(/\{input(\d+)\}/g)].map((m) => Number(m[1]));
    return [...new Set(referenced)].filter((n) => n < 1 || n > c.sources.length).sort((a, b) => a - b);
  };

  const specProblems = (c: DetectionCondition | null): string[] => (c?.spec ? validateSpec(c.spec) : []);

  const isFormValid = (): boolean => {
    if (!editingCondition) return false;
    // Structured rules are checked against the shared spec validator; the
    // free-text checks below don't apply to them.
    if (editingCondition.spec) return specProblems(editingCondition).length === 0;
    if (!editingCondition.sources.length || !editingCondition.sources.every(isSourceValid)) return false;
    if (!editingCondition.instruction?.trim()) return false;
    if (!hasStepBoundSource(editingCondition)) return false;
    if (danglingPlaceholders(editingCondition).length > 0) return false;
    return true;
  };

  // ==================== HELPERS ====================

  const getStepFields = (stepUid: string, fieldType?: 'text' | 'image' | 'date') => {
    const step = steps.find((s: any) => s.uid === stepUid);
    if (!step?.component?.fields) return [];
    const imageComponents = ['CAMERA', 'FILE'];
    const textComponents = [
      'TEXT',
      'NUMBER',
      'SELECT',
      'CHECKBOX',
      'RADIO',
      'DATE',
      'TEXTARRAY',
      'SWITCH',
      'RESOURCE',
      'PASSWORD',
      'ESIGN'
    ];
    if (!fieldType) return step.component.fields;
    if (fieldType === 'image') {
      const filtered = step.component.fields.filter((f: any) => imageComponents.includes(f.inputComponent));
      // If no explicit image fields, but step type is IMAGE/DOCUMENT, show all fields
      if (filtered.length === 0 && ['IMAGE', 'DOCUMENT'].includes(step.component?.componentType || step.stepType)) {
        return step.component.fields;
      }
      return filtered;
    }
    if (fieldType === 'date') {
      return step.component.fields.filter((f: any) => f.inputComponent === 'DATE');
    }
    return step.component.fields.filter((f: any) => textComponents.includes(f.inputComponent));
  };

  const getResourceTypeFields = (rtId: string) => {
    const rt = resourceTypes.find((r: any) => r.id === rtId || r._id === rtId);
    return rt?.fields || [];
  };

  // ==================== RENDER: SOURCE ROW ====================

  const renderSourceRow = (srcIndex: number, source: SourceConfig) => {
    const canRemove = (editingCondition?.sources.length || 0) > 1;

    const renderFields = () => {
      if (source.sourceType === 'step_text_field' || source.sourceType === 'step_image_field' || source.sourceType === 'step_date_field') {
        const fieldType: 'text' | 'image' | 'date' =
          source.sourceType === 'step_image_field' ? 'image' : source.sourceType === 'step_date_field' ? 'date' : 'text';
        const emptyLabel = fieldType === 'image' ? 'image' : fieldType === 'date' ? 'date' : 'text';
        return (
          <>
            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Step</InputLabel>
              <Select
                value={source.stepUid || ''}
                label="Step"
                onChange={(e) => updateSource(srcIndex, { stepUid: e.target.value, fieldUid: '' })}
              >
                {steps.map((s: any) => (
                  <MenuItem key={s.uid} value={s.uid}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={source.fieldUid || ''}
                label="Field"
                disabled={!source.stepUid}
                onChange={(e) => updateSource(srcIndex, { fieldUid: e.target.value })}
              >
                {source.stepUid &&
                  (() => {
                    const fields = getStepFields(source.stepUid, fieldType);
                    if (fields.length === 0)
                      return (
                        <MenuItem disabled>
                          <em>No {emptyLabel} fields in this step</em>
                        </MenuItem>
                      );
                    return fields.map((f: any) => (
                      <MenuItem key={f.uid} value={f.uid}>
                        {f.question || f.name}
                      </MenuItem>
                    ));
                  })()}
              </Select>
            </FormControl>
          </>
        );
      }
      if (source.sourceType === 'step_location') {
        return (
          <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel>Step (GPS captured on submission)</InputLabel>
            <Select
              value={source.stepUid || ''}
              label="Step (GPS captured on submission)"
              onChange={(e) => updateSource(srcIndex, { stepUid: e.target.value })}
            >
              {steps.map((s: any) => (
                <MenuItem key={s.uid} value={s.uid}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      if (source.sourceType === 'step_submission_date') {
        return (
          <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
            <InputLabel>Step (created_at captured on submission)</InputLabel>
            <Select
              value={source.stepUid || ''}
              label="Step (created_at captured on submission)"
              onChange={(e) => updateSource(srcIndex, { stepUid: e.target.value })}
            >
              {steps.map((s: any) => (
                <MenuItem key={s.uid} value={s.uid}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      if (source.sourceType === 'resource_type_field') {
        return (
          <>
            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Resource Type</InputLabel>
              <Select
                value={source.resourceTypeId || ''}
                label="Resource Type"
                onChange={(e) => updateSource(srcIndex, { resourceTypeId: e.target.value, fieldUid: '' })}
              >
                {resourceTypes.map((rt: any) => (
                  <MenuItem key={rt.id || rt._id} value={rt.id || rt._id}>
                    {rt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={source.fieldUid || ''}
                label="Field"
                disabled={!source.resourceTypeId}
                onChange={(e) => updateSource(srcIndex, { fieldUid: e.target.value })}
              >
                {source.resourceTypeId &&
                  getResourceTypeFields(source.resourceTypeId).map((f: any) => (
                    <MenuItem key={f.name} value={f.name}>
                      {f.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </>
        );
      }
      if (source.sourceType === 'custom') {
        return (
          <>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={source.customType || 'text'}
                label="Type"
                onChange={(e) => updateSource(srcIndex, { customType: e.target.value as 'text' | 'image', customValue: '' })}
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
            {source.customType === 'image' ? (
              source.customValue ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Box
                    component="img"
                    src={source.customValue}
                    alt="uploaded"
                    sx={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 0.5, border: '1px solid', borderColor: 'grey.300' }}
                    onError={(e: any) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                    {source.customValue.split('/').pop()}
                  </Typography>
                  <IconButton size="small" onClick={() => updateSource(srcIndex, { customValue: '' })}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !apiContext) return;
                      try {
                        const res = await apiContext.uploadAsset(file, 'detection/custom');
                        if (res?.data?.public_url) updateSource(srcIndex, { customValue: res.data.public_url });
                        else if (res?.data?.file_path) updateSource(srcIndex, { customValue: res.data.file_path });
                      } catch (err) {
                        console.error('Upload failed:', err);
                      }
                    }}
                  />
                </Button>
              )
            ) : (
              <TextField
                size="small"
                sx={{ flex: 1 }}
                label="Text Value"
                placeholder="Enter text value"
                value={source.customValue || ''}
                onChange={(e) => updateSource(srcIndex, { customValue: e.target.value })}
              />
            )}
          </>
        );
      }
      return null;
    };

    const tag = getInputTag(srcIndex);

    return (
      <Box
        key={srcIndex}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          p: 1,
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        <Chip
          label={tag}
          size="small"
          variant="filled"
          color="primary"
          sx={{ fontWeight: 600, minWidth: 65, fontFamily: 'monospace', fontSize: '0.72rem' }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={source.sourceType}
            label="Type"
            onChange={(e) =>
              updateSource(srcIndex, {
                sourceType: e.target.value as SourceConfig['sourceType'],
                stepUid: undefined,
                fieldUid: undefined,
                resourceTypeId: undefined,
                customValue: '',
                customType: 'text'
              })
            }
          >
            <MenuItem value="step_text_field">Text Field</MenuItem>
            <MenuItem value="step_image_field">Image Field</MenuItem>
            <MenuItem value="step_date_field">Date Field</MenuItem>
            <MenuItem value="step_location">Location</MenuItem>
            <MenuItem value="step_submission_date">Step Submission Date</MenuItem>
            <MenuItem value="resource_type_field">Resource Type Field</MenuItem>
            <MenuItem value="custom">Custom Field</MenuItem>
          </Select>
        </FormControl>
        {renderFields()}
        {canRemove && (
          <IconButton size="small" color="error" onClick={() => removeSource(srcIndex)} sx={{ flexShrink: 0 }}>
            <Delete fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  // ==================== MAIN RENDER ====================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          AI Detection Settings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ==================== LIST TABLE ==================== */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ mb: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }}
              >
                Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }}
              >
                Inputs
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }}
              >
                Instruction
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }}
              >
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No detection conditions configured
                </TableCell>
              </TableRow>
            ) : (
              conditions.map((c, idx) => (
                <TableRow
                  key={c.uid || idx}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    ...(editingIndex === idx && { bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.50' } })
                  }}
                >
                  <TableCell sx={{ py: 1.5, fontWeight: 500 }}>
                    {c.name || (
                      <Typography component="span" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        Unnamed
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {c.spec ? (
                        <>
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={CHECK_TYPES[c.spec.checkType]?.label || c.spec.checkType}
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                          />
                          <Chip size="small" variant="outlined" label="no AI" sx={{ fontSize: '0.68rem', height: 22 }} />
                        </>
                      ) : (
                        <>
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Prompt"
                            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                          />
                          {c.sources.map((s, i) => (
                            <Chip
                              key={i}
                              size="small"
                              variant="outlined"
                              color="primary"
                              label={`input${i + 1}: ${
                                {
                                  step_text_field: 'Text Field',
                                  step_image_field: 'Image Field',
                                  step_date_field: 'Date Field',
                                  step_location: 'Location',
                                  step_submission_date: 'Submission Date',
                                  resource_type_field: 'Resource Type',
                                  custom: 'Custom'
                                }[s.sourceType] || s.sourceType
                              }`}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          ))}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220, py: 1.5 }}>
                    {(() => {
                      // Structured rules show their generated sentence, so the
                      // list reads the same way as the form's echo.
                      const text = c.spec ? describeRule(c.spec) : c.instruction;
                      return (
                        <Tooltip title={text || ''} arrow placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 220,
                              color: 'text.secondary',
                              fontSize: '0.8rem'
                            }}
                          >
                            {text || '—'}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {/* A draft looks nothing like a disabled rule — one was
                          never finished, the other was switched off. */}
                      {c.publishState === 'draft' ? (
                        <Chip
                          label="Draft"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                        />
                      ) : c.publishState === 'archived' ? (
                        <Chip label="Archived" size="small" sx={{ fontWeight: 500, fontSize: '0.7rem', height: 22, bgcolor: 'grey.200' }} />
                      ) : (
                        <Chip
                          label={c.enabled ? 'Live' : 'Paused'}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 22,
                            bgcolor: c.enabled ? 'success.light' : 'grey.200',
                            color: c.enabled ? 'success.dark' : 'text.secondary'
                          }}
                        />
                      )}
                      {c.doubleCheck && (
                        <Tooltip title="Each check runs twice; a stronger model decides when the two disagree">
                          <Chip label="Double-check" size="small" variant="outlined" color="info" sx={{ fontSize: '0.66rem', height: 22 }} />
                        </Tooltip>
                      )}
                      {c._state?.hasUnpublishedChanges && c.publishState === 'published' && (
                        <Tooltip title="Edited since it was last published — the published version is what runs">
                          <Chip label="edited" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.66rem', height: 22 }} />
                        </Tooltip>
                      )}
                      {(c._state?.liveVersion ?? 0) > 0 && (
                        <Chip
                          label={`v${c._state?.liveVersion}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.66rem', height: 22 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      {/* span holds the tooltip's ref, so it still fires once
                          PermissionWrapper has disabled the button underneath */}
                      <span>
                        <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(idx)}
                            sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </PermissionWrapper>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(idx)}
                            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </PermissionWrapper>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ==================== FORM ==================== */}
      <div ref={formRef}>
        {editingCondition ? (
          <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {editingIndex !== null ? 'Edit Condition' : 'New Condition'}
                </Typography>
                <IconButton size="small" onClick={handleCancel}>
                  <Close />
                </IconButton>
              </Box>

              {/* Name + Enabled */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <TextField
                  size="small"
                  label="Condition Name"
                  value={editingCondition.name}
                  onChange={(e) => updateField({ name: e.target.value })}
                  placeholder="e.g. Face Verification"
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  control={<Switch checked={editingCondition.enabled} onChange={(e) => updateField({ enabled: e.target.checked })} />}
                  label="Enabled"
                  sx={{ mr: 0 }}
                />
              </Box>

              {/* Double-check — only meaningful where an AI makes the call; an
                  exact (deterministic) check would just agree with itself. */}
              {(!editingCondition.spec || isAiCheck(editingCondition.spec.checkType)) && (
                <Box sx={{ mb: 2.5, px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!editingCondition.doubleCheck}
                        onChange={(e) => updateField({ doubleCheck: e.target.checked })}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Double-check
                      </Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 5.5 }}>
                    The AI checks twice. If the two answers differ, a stronger model makes the final call. More accurate,
                    but every check costs at least twice as much. Takes effect without re-publishing.
                  </Typography>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* ── Structured rule ─────────────────────────────────────── */}
              {editingCondition.spec ? (
                <>
                  {draftAssumptions.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2, py: 0.5 }} onClose={() => setDraftAssumptions([])}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                        AI-drafted — please check these assumptions
                      </Typography>
                      {draftAssumptions.map((a, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                          • {a}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                  <RuleBuilder
                    spec={editingCondition.spec}
                    onChange={(spec) => updateField({ spec })}
                    steps={steps}
                    resourceTypes={resourceTypes}
                    providers={providers}
                  />
                  {specProblems(editingCondition).length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                      {specProblems(editingCondition).map((p, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                          {p}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                  <Divider sx={{ my: 2 }} />
                </>
              ) : (
                <>
                  {/* ── Legacy prompt rule ──────────────────────────────────── */}
                  {/* These behave differently from anything built since, and the
                      differences are invisible unless we say so — particularly
                      that saving goes live immediately, with no publish step. */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      This is an older prompt-style rule
                    </Typography>
                    <Typography variant="caption" component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
                      <li>It keeps running exactly as it always has — nothing about it changed.</li>
                      {!isLegacyGated(editingCondition) && <li>Saving takes effect immediately; there is no publish step.</li>}
                      <li>It runs on the older engine and can’t choose a model. Rebuild it as a structured check to get that.</li>
                    </Typography>
                  </Alert>

                  {/* Sources */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                    Inputs <span style={{ color: '#f44336' }}>*</span>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Data fed to the AI — select from step fields, resource fields, or provide custom values.
                  </Typography>
                  {editingCondition.sources.map((source, i) => renderSourceRow(i, source))}
                  {editingCondition.sources.every(isSourceValid) && !hasStepBoundSource(editingCondition) && (
                    <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                      At least one input has to come from a workflow step. A rule is only checked when a step it uses is submitted, so this
                      one would never run.
                    </Alert>
                  )}
                  <Button variant="text" size="small" startIcon={<Add />} onClick={addSource} sx={{ mb: 2 }}>
                    Add Input
                  </Button>

                  <Divider sx={{ mb: 2 }} />

                  {/* Instruction */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                    Instruction <span style={{ color: '#f44336' }}>*</span>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                    Prompt sent to the AI model. Reference your inputs as <strong>{'{input1}'}</strong>, <strong>{'{input2}'}</strong>,{' '}
                    <strong>{'{input3}'}</strong>, etc.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editingCondition.instruction}
                    onChange={(e) => updateField({ instruction: e.target.value })}
                    multiline
                    rows={3}
                    placeholder="e.g. Check if {input1} contains the text value of {input2}."
                    error={!editingCondition.instruction.trim() && editingCondition.sources.some(isSourceValid)}
                    helperText={
                      !editingCondition.instruction.trim() && editingCondition.sources.some(isSourceValid) ? 'Instruction is required' : ''
                    }
                    sx={{ mb: 1.5 }}
                  />
                  {danglingPlaceholders(editingCondition).length > 0 && (
                    <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
                      The instruction refers to{' '}
                      {danglingPlaceholders(editingCondition)
                        .map((n) => `{input${n}}`)
                        .join(', ')}
                      , but this rule has {editingCondition.sources.length} input
                      {editingCondition.sources.length === 1 ? '' : 's'}.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.25 }}>
                      Or pick a template:
                    </Typography>
                    {TEMPLATE_INSTRUCTIONS.map((t, i) => (
                      <Chip
                        key={i}
                        label={t.label}
                        size="small"
                        variant={editingCondition.instruction === t.value ? 'filled' : 'outlined'}
                        color={editingCondition.instruction === t.value ? 'primary' : 'default'}
                        onClick={() => updateField({ instruction: t.value })}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  {isFaceComparisonInstruction(editingCondition.instruction) && (
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      label="Uses Face Recognition Service"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ mb: 2, fontWeight: 500 }}
                    />
                  )}

                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              {/* Notification (Optional) */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Notification{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (Optional)
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                If configured, the AI detection response will be sent to the selected recipients.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Notify on</InputLabel>
                    <Select
                      multiple
                      value={editingCondition.notification.onOutcomes || []}
                      label="Notify on"
                      onChange={(e) => updateNotification({ onOutcomes: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => (
                            <Chip key={v} size="small" label={OUTCOME_OPTIONS.find((o) => o.value === v)?.label || v} />
                          ))}
                        </Box>
                      )}
                    >
                      {OUTCOME_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Which results are worth an alert. Leaving Match off is usually right — a rule that pings on every pass trains people to
                    ignore it.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Channels</InputLabel>
                    <Select
                      multiple
                      value={editingCondition.notification.channels || []}
                      label="Channels"
                      onChange={(e) => updateNotification({ channels: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => (
                            <Chip key={v} label={v} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {NOTIFICATION_CHANNELS.map((ch) => (
                        <MenuItem key={ch} value={ch}>
                          {ch}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Roles</InputLabel>
                    <Select
                      multiple
                      value={editingCondition.notification.roles || []}
                      label="Roles"
                      onChange={(e) => updateNotification({ roles: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => {
                            const rt = resourceTypes.find((r: any) => (r.id || r._id) === v);
                            return <Chip key={v} label={rt?.name || v} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {resourceTypes.map((rt: any) => (
                        <MenuItem key={rt.id || rt._id} value={rt.id || rt._id}>
                          {rt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!editingCondition.notification.transactionOnly}
                        onChange={(e) => updateNotification({ transactionOnly: e.target.checked })}
                      />
                    }
                    label={
                      <Typography variant="caption">
                        Only notify users in this transaction — role recipients are limited to drivers assigned to or active on this
                        transaction (instead of everyone in the process).
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Users</InputLabel>
                    <Select
                      multiple
                      value={editingCondition.notification.users || []}
                      label="Users"
                      onChange={(e) => updateNotification({ users: e.target.value as string[] })}
                      renderValue={(sel) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(sel as string[]).map((v) => {
                            const u = users.find((x) => x.uid === v);
                            return <Chip key={v} label={u?.name || v} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {users.length === 0 ? (
                        <MenuItem disabled>
                          <em>No team users found</em>
                        </MenuItem>
                      ) : (
                        users.map((u) => (
                          <MenuItem key={u.uid} value={u.uid}>
                            {u.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* ── Test before it can go live ─────────────────────────── */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                Test this rule
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Run it against a real past transaction — no need to save first, and nothing is recorded against that transaction.
              </Typography>
              <PreviewPanel
                teamUid={teamUid}
                rule={editingCondition}
                disabled={!isFormValid()}
                rerunToken={rerunPreviewToken}
                onPreviewed={async () => {
                  setEditedSincePreview(false);
                  setPreviewedThisSession(true);
                  // Silent: a full refresh would flip `loading`, unmount this
                  // form, and take the result the user just asked for with it.
                  await loadData(true);
                }}
              />

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pt: 2 }}>
                <Box>
                  {editingCondition.publishState === 'draft' && (
                    <Chip size="small" color="warning" variant="outlined" label="Draft — not running yet" sx={{ fontWeight: 600 }} />
                  )}
                  {/* An ungated rule is live the moment you save. Publishing it
                      snapshots it and swaps that for the review flow, which is a
                      real change to how the rule behaves — say so before it
                      happens rather than after. */}
                  {savesGoLiveImmediately(editingCondition) && (
                    <Tooltip title="Publishing takes a snapshot of this rule. After that, edits wait for a re-publish instead of going live as soon as you save.">
                      <Chip size="small" variant="outlined" label="Live — edits apply on save" sx={{ fontWeight: 600 }} />
                    </Tooltip>
                  )}
                  {editingCondition.uid && !isTested(editingCondition) && (
                    <Tooltip title="Publishing is allowed, but running the rule against a real transaction first is recommended.">
                      <Chip size="small" color="info" variant="outlined" label="Not tested" sx={{ fontWeight: 600, ml: 0.5 }} />
                    </Tooltip>
                  )}
                  {editingCondition.publishState === 'published' && hasUnpublishedEdits(editingCondition) && (
                    <Chip size="small" color="warning" variant="outlined" label="Unpublished changes" sx={{ fontWeight: 600 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                  {/* "Save draft" would be a lie on a rule whose saves go
                      straight live — which is every legacy rule. */}
                  <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                    <Button variant="outlined" startIcon={<Save />} onClick={handleSave} disabled={saving || !isFormValid()}>
                      {saving ? 'Saving...' : savesGoLiveImmediately(editingCondition) ? 'Save changes' : 'Save draft'}
                    </Button>
                  </PermissionWrapper>
                  {/* Testing first is recommended but not required. */}
                  <Tooltip title={!editingCondition.uid ? 'Save the rule first' : ''}>
                    <span>
                      <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                        <Button
                          variant="contained"
                          onClick={handlePublish}
                          disabled={publishing || saving || !editingCondition.uid || !isFormValid()}
                        >
                          {publishing ? 'Publishing…' : 'Publish'}
                        </Button>
                      </PermissionWrapper>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ borderStyle: 'dashed' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
                Add a detection rule
              </Typography>

              {/* Describe it in your own words and let the model draft the
                  structured rule. It only ever produces a spec for you to
                  review — the form opens pre-filled and nothing is saved until
                  you accept it. */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2, mt: 1 }}>
                <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="e.g. make sure the driver's licence hasn't expired when they arrive"
                    value={draftRequest}
                    onChange={(e) => setDraftRequest(e.target.value)}
                    helperText="Describe the check in plain English — we'll draft it for you to review."
                  />
                </PermissionWrapper>
                <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                  <Button
                    variant="contained"
                    onClick={handleDraft}
                    disabled={drafting || !draftRequest.trim()}
                    sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                  >
                    {drafting ? <CircularProgress size={16} /> : 'Build the rule'}
                  </Button>
                </PermissionWrapper>
              </Box>

              <Divider sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  or start from a check type
                </Typography>
              </Divider>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                These run as exact comparisons — no AI, no cost, same answer every time.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {DETERMINISTIC_CHECK_TYPES.map((d) => (
                  <Tooltip key={d.type} title={d.summary} arrow>
                    <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                      <Chip
                        label={d.label}
                        onClick={() => handleAdd(d.type)}
                        color="primary"
                        variant="outlined"
                        sx={{ cursor: 'pointer', fontWeight: 500 }}
                      />
                    </PermissionWrapper>
                  </Tooltip>
                ))}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {/* The escape hatch: still there, never the default.
                  Creates a CUSTOM_AI structured rule rather than a legacy prompt
                  one, so a hand-written check still gets the model picker,
                  structured output and a confidence threshold. The legacy shape
                  stays readable and editable for rules that already use it, but
                  nothing new is created into it. */}
              <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_AI_DETECTION} disableInsteadOfHide={true}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Add />}
                  onClick={() => handleAdd('CUSTOM_AI')}
                  sx={{ textTransform: 'none' }}
                >
                  Advanced: write a prompt instead
                </Button>
              </PermissionWrapper>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                For checks that need judgement — document authenticity, image quality, face matching. Interpreted by an AI model, so results
                can vary. You choose the model.
              </Typography>
            </CardContent>
          </Card>
        )}
      </div>
    </Box>
  );
};

export default DetectionSettings;
